import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

import version from './version.json';

const EXPECTED_NOT_FOUND = /\/client-api\/v1\/custom-settings\//;

const isExpectedHttpFailure = (event: Sentry.ErrorEvent): boolean => {
  const url = event.request?.url ?? '';
  const status = event.contexts?.['response']?.['status_code'];

  return status === 404 && EXPECTED_NOT_FOUND.test(url);
};

const isWalletExtensionRejection = (event: Sentry.ErrorEvent): boolean => {
  const serialized = event.extra?.['__serialized__'] as
    { code?: unknown; message?: unknown } | undefined;

  return typeof serialized?.code === 'number' && typeof serialized.message === 'string';
};

if (environment.production) {
  enableProdMode();
}

Sentry.init({
  dsn: 'https://10452d88a316c6ca09d1d0c2754e6626@o4507503317090304.ingest.us.sentry.io/4507617060323328',
  integrations: [
    // Registers and configures the Tracing integration,
    // which automatically instruments your application to monitor its
    // performance, including custom Angular routing instrumentation
    Sentry.browserTracingIntegration({}),
    // Registers the Replay integration,
    // which automatically captures Session Replays
    Sentry.replayIntegration(),

    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [400, [402, 599]],
      failedRequestTargets: [/^(?!.*\/cdn-cgi\/).*$/],
    }),
  ],
  enabled:
    window.location.hostname !== 'localhost' &&
    !/HeadlessChrome/.test(navigator.userAgent),
  environment: window.location.hostname,
  ignoreErrors: [
    /Http failure response for \S*\/assets\/i18n\/\S*: (0|429) /,
    'runtime.sendMessage',
  ],
  denyUrls: [
    /\/cdn-cgi\//,
    /static\.cloudflareinsights\.com/,
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari-extension:\/\//,
    /^safari-web-extension:\/\//,
    /webkit-masked-url:/,
    /frame_ant\.js/,
  ],
  beforeSend: event =>
    isExpectedHttpFailure(event) || isWalletExtensionRejection(event) ? null : event,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  release: version.version,

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: ['localhost'],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
});
platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch(err => console.error(err));
